import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeliveryType, Notification, getNiceDate, isValidBusinessObject } from '@iotv/datamodel';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailIcon from '@mui/icons-material/Mail';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SmsIcon from '@mui/icons-material/Sms';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useMemo, useRef, useState } from 'react';
import config from '../../../config';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { isAdminRole } from '../../../helpers/access';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import { SingleEntityAutoQuery } from '../../generic/EntityAutoQuery';
import { IncidentView } from '../Incidents/IncidentView';
const debug = process.env.REACT_APP_DEBUG && false;
const dynamo = config.aws.dynamoDB;
const NotificationCard = (props) => {
    const { notification, acknowledge, ...rest } = props;
    const [showIncident, setShowIncident] = useState(false);
    let MethodIcon = undefined;
    switch (notification.deliveryType) {
        case DeliveryType.EMAIL:
            MethodIcon = _jsx(MailIcon, { fontSize: "large" });
            break;
        case DeliveryType.SMS:
            MethodIcon = _jsx(SmsIcon, { fontSize: "large" });
            break;
        case DeliveryType.MOBILE_PUSH:
            MethodIcon = _jsx(SmartphoneIcon, { fontSize: "large" });
            break;
    }
    return (_jsx(Card, { children: _jsx(CardContent, { children: _jsxs(Grid, { container: true, sx: { rowGap: 1 }, children: [_jsxs(Grid, { container: true, sx: { columnGap: 1 }, children: [_jsx(Grid, { children: notification.acknowledged ? _jsx(DoneAllIcon, { color: "success", fontSize: "large" }) : _jsx(CheckIcon, { color: "warning", fontSize: "large" }) }), _jsx(Grid, { children: MethodIcon }), _jsx(Grid, { children: _jsx(Typography, { variant: "h5", children: notification.messageTitle }) }), _jsx(Grid, { children: _jsxs(Button, { variant: "contained", size: "small", disabled: notification.acknowledged, startIcon: _jsx(CheckIcon, {}), color: notification.acknowledged ? 'success' : notification.success ? 'warning' : 'error', onClick: notification.acknowledged ? undefined : acknowledge, children: ["Acknowledge", notification.acknowledged ? 'd' : ''] }) })] }), _jsx(Grid, { size: 12, children: _jsx(Typography, { children: notification.messageContent }) }), notification.incidentSk && (_jsx(Grid, { size: 12, children: _jsxs(Button, { variant: "outlined", size: "small", startIcon: _jsx(WarningIcon, {}), endIcon: showIncident ? _jsx(ExpandLessIcon, {}) : _jsx(ExpandMoreIcon, {}), onClick: () => setShowIncident((show) => !show), children: [showIncident ? 'Hide' : 'Show', " incident"] }) })), notification.incidentSk && showIncident && _jsx(IncidentView, { ...rest, incidentSk: notification.incidentSk })] }) }) }));
};
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const notificationColumns = [
    {
        field: 'success',
        headerName: 'Status',
        ...columnLayout,
        width: 65,
        type: 'custom',
        renderCell(params) {
            let SentIcon = undefined;
            switch (params.value) {
                case true:
                    SentIcon = params.row.acknowledged ? _jsx(DoneAllIcon, { color: "success" }) : _jsx(CheckIcon, { color: "warning" });
                    break;
                case false:
                    SentIcon = _jsx(WarningIcon, { color: "warning" });
                    break;
            }
            return _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: SentIcon });
        },
    },
    {
        field: 'deliveryType',
        headerName: 'Type',
        ...columnLayout,
        width: 60,
        type: 'custom',
        renderCell(params) {
            let TypeIcon = undefined;
            switch (params.value) {
                case DeliveryType.EMAIL:
                    TypeIcon = _jsx(MailIcon, {});
                    break;
                case DeliveryType.SMS:
                    TypeIcon = _jsx(SmsIcon, {});
                    break;
                case DeliveryType.MOBILE_PUSH:
                    TypeIcon = _jsx(SmartphoneIcon, {});
                    break;
            }
            return _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: TypeIcon });
        },
    },
    {
        field: 'timestamp',
        headerName: 'Time',
        ...columnLayout,
        minWidth: 150,
        type: 'dateTime',
        valueFormatter(value) {
            return getNiceDate(value);
        },
    },
    {
        field: 'messageTitle',
        headerName: 'Title',
        ...columnLayout,
        minWidth: 100,
        renderCell(params) {
            return _jsx("div", { style: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: params.value });
        },
    },
    {
        field: 'messageContent',
        headerName: 'Message',
        ...columnLayout,
        minWidth: 300,
        flex: 1,
        renderCell(params) {
            return _jsx("div", { style: { overflow: 'hidden', textOverflow: 'ellipsis' }, children: params.value });
        },
    },
];
const columns = [DataGridRadioColumn, ...notificationColumns];
export const NotificationsTab = ({ contextCustomer, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, userFunctions, transactionFunctions, isSmallView, }) => {
    const isAdmin = isAdminRole(userSelectedGroup);
    const [selectedUser, setSelectedUser] = useState(user ?? null);
    // data
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState(undefined);
    // database pagination querying
    const mapPageToNextCursor = useRef({ 0: undefined });
    // pagination state
    const pageSize = 10;
    const [rowCount, setRowCount] = useState(-1);
    const [hasNextPage, setHasNextPage] = useState(undefined);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize,
    });
    const paginationMetaRef = useRef();
    const paginationMeta = useMemo(() => {
        if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage)
            paginationMetaRef.current = { hasNextPage };
        return paginationMetaRef.current;
    }, [hasNextPage]);
    useEffect(() => {
        if (paginationMeta?.hasNextPage !== false)
            setRowCount(-1);
    }, [paginationMeta?.hasNextPage]);
    // row selection
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    useEffect(() => {
        const selectedIds = 'ids' in rowSelectionModel ? Array.from(rowSelectionModel.ids) : rowSelectionModel;
        setSelectedNotification(notifications.find((n) => n.id === selectedIds[0]));
    }, [rowSelectionModel, notifications]);
    const fetch = async (startKey) => {
        setLoading(true);
        let items = [];
        const { err, data } = await ConsumerDao.baseQuery({
            TableName: dynamo.tableName,
            IndexName: dynamo.tsiName,
            ScanIndexForward: false,
            ExpressionAttributeNames: {
                '#pk': dynamo.pKey,
                '#sk': dynamo.sKey,
            },
            ExpressionAttributeValues: {
                ':pk': isAdmin ? selectedUser?.sk : user?.sk,
                ':sfv': 'Notification',
            },
            KeyConditionExpression: '#pk = :pk',
            FilterExpression: 'begins_with(#sk, :sfv)',
            Limit: pageSize,
            ExclusiveStartKey: startKey,
        });
        if (err)
            return;
        if (data?.Items)
            items = data?.Items.map((item) => new Notification(item));
        setNotifications(items);
        setHasNextPage(data?.LastEvaluatedKey !== undefined);
        setLoading(false);
        // set the start key for the next page
        mapPageToNextCursor.current[paginationModel.page + 1] = data?.LastEvaluatedKey;
    };
    useEffect(() => {
        if (contextCustomer && selectedUser)
            fetch(mapPageToNextCursor.current[paginationModel.page]);
    }, [contextCustomer?.sk, isAdmin, selectedUser?.sk, user?.sk, paginationModel]);
    return (_jsxs(Stack, { minHeight: "40cqh", marginBlockStart: isAdmin ? 1 : 0, gap: 1, sx: { transition: 'margin-block-start 0.15s ease-in-out' }, children: [isAdmin && (_jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Typography, { children: "View notifications for" }), _jsx(SingleEntityAutoQuery, { contextObject: contextCustomer, entityType: "User", currentEntity: selectedUser, setCurrentEntity: (u) => u && isValidBusinessObject(u) && setSelectedUser(u), label: "User", fullWidth: false })] })), _jsx(DataGrid, { loading: loading, rows: notifications, columns: columns, checkboxSelection: true, pageSizeOptions: [pageSize], paginationMode: "server", rowCount: rowCount, onRowCountChange: setRowCount, paginationMeta: paginationMeta, paginationModel: paginationModel, onPaginationModelChange: (model) => {
                    if (model.page === 0 || mapPageToNextCursor.current[model.page])
                        setPaginationModel(model);
                }, hideFooterSelectedRowCount: true, disableMultipleRowSelection: true, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel }), selectedNotification && (_jsx(NotificationCard, { contextCustomer: contextCustomer, user: user, userGroupRoles: userGroupRoles, userSelectedGroup: userSelectedGroup, roleArbitrationInstance: roleArbitrationInstance, userFunctions: userFunctions, transactionFunctions: transactionFunctions, isSmallView: isSmallView, notification: selectedNotification, acknowledge: () => {
                    setNotifications((n) => {
                        const i = n.findIndex((notification) => notification.id === selectedNotification.id);
                        const newNotification = new Notification(n[i]);
                        newNotification.acknowledgeNotification();
                        ConsumerDao.save(newNotification);
                        setSelectedNotification(newNotification);
                        return n.toSpliced(i, 1, newNotification);
                    });
                } }, selectedNotification.sk))] }, "NotificationsTabMainGrid"));
};
